import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../context/AuthContext";
import { ShieldCheck, ArrowRight, FileWarning, CheckCircle2, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MISSING_LABELS = {
  f01_risks: "F.01 Riesgos",
  f02_collaborators: "F.02 Colaboradores",
  quarterly_review: "Seguimiento Q",
  approval: "Aprobación",
};

const CierreBadge = ({ cierreStats, navigate }) => {
  if (!cierreStats || cierreStats.needs_action === 0) return null;
  return (
    <Button
      size="sm" variant="outline"
      className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
      onClick={() => navigate("/pg07/cierre-dashboard")}
      data-testid="pg07-widget-cierre-badge"
    >
      <Award className="w-3 h-3 mr-1" />
      {cierreStats.needs_action} cierre{cierreStats.needs_action !== 1 ? "s" : ""} pendiente{cierreStats.needs_action !== 1 ? "s" : ""}
      <ArrowRight className="w-3 h-3 ml-1" />
    </Button>
  );
};

/** Inline reminder card — shows on /inicio when the current user (PM) has PG-07
 * fichas pending. Hidden if there are none.
 * Fix 2026-08-24 (repass2): para PMs normales, "sin pendientes en tus proyectos"
 * es correcto. Pero para un ADMIN (que normalmente no es PM de nada) sonaba a
 * "PG-07 al día" cuando en realidad había proyectos rojos/sin evaluar a nivel
 * organización — ahora si eres admin y tus proyectos están limpios, se comprueba
 * también el estado global antes de mostrar el verde. */
export function PG07ReminderWidget() {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cierreStats, setCierreStats] = useState(null);
  const [orgCounts, setOrgCounts] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const calls = [
          fetch(`${API}/pg07/compliance/mine`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/pg07/cierre-dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        ];
        if (isAdmin) calls.push(fetch(`${API}/pg07/compliance`, { headers: { Authorization: `Bearer ${token}` } }));
        const [r1, r2, r3] = await Promise.all(calls);
        if (r1.ok) { const j = await r1.json(); if (!cancelled) setData(j); }
        if (r2.ok) { const j = await r2.json(); if (!cancelled) setCierreStats(j.stats); }
        if (r3?.ok) { const j = await r3.json(); if (!cancelled) setOrgCounts(j.counts); }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [token, isAdmin]);

  if (!data) return null;

  const orgCritical = (orgCounts?.red || 0) + (orgCounts?.sin_evaluar || 0);

  // Admin sin proyectos propios pendientes, pero la organización SÍ tiene rojos/sin
  // evaluar → no mostrar "al día" (sería falso a nivel global).
  if (data.pending_count === 0 && isAdmin && orgCritical > 0) {
    return (
      <Card className="border-amber-300/70 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800/40"
            data-testid="pg07-reminder-org-alert">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-amber-700 dark:text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                PG-07 · sin pendientes en tus proyectos, pero {orgCounts.red} "No conforme" y {orgCounts.sin_evaluar} sin evaluar en la organización
              </div>
              <div className="text-xs text-amber-700/80 dark:text-amber-300/80">
                No es "PG-07 al día" a nivel global — revisa la Matriz de Cumplimiento.
              </div>
            </div>
            <CierreBadge cierreStats={cierreStats} navigate={navigate} />
            <Button size="sm" variant="outline" onClick={() => navigate("/pg07")} data-testid="pg07-reminder-goto">
              Ir al PG-07 <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Nothing pending → subtle "all good" state (optional)
  if (data.pending_count === 0) {
    return (
      <Card className="border-emerald-200/60 bg-emerald-50/40 dark:bg-emerald-950/10 dark:border-emerald-800/40"
            data-testid="pg07-reminder-complete">
        <CardContent className="p-3 flex items-center gap-3 text-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="flex-1 text-emerald-800 dark:text-emerald-300">
            <strong>PG-07 al día</strong> · sin fichas pendientes en tus proyectos.
          </span>
          <CierreBadge cierreStats={cierreStats} navigate={navigate} />
          <Button size="sm" variant="ghost" onClick={() => navigate("/pg07")}
                  data-testid="pg07-reminder-goto">
            <ArrowRight className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-200/70 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900/40"
          data-testid="pg07-reminder-widget">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-red-700 dark:text-red-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-800 dark:text-red-200">
              PG-07 · {data.pending_count} proyecto{data.pending_count !== 1 ? "s" : ""} con fichas pendientes
            </div>
            <div className="text-xs text-red-700/80 dark:text-red-300/80">
              Cumple el procedimiento antes de que cierre el trimestre {data.quarter} {data.year}.
            </div>
          </div>
          <CierreBadge cierreStats={cierreStats} navigate={navigate} />
          <Button size="sm" variant="outline" onClick={() => navigate("/pg07")} data-testid="pg07-reminder-goto">
            Ir al PG-07 <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
        <div className="space-y-1.5 mt-3">
          {data.pending.slice(0, 4).map((p) => (
            <div key={p.project_id} className="flex items-center gap-2 flex-wrap text-xs" data-testid={`pg07-reminder-item-${p.project_id}`}>
              <FileWarning className="w-3 h-3 text-red-600" />
              <span className="font-medium truncate max-w-[220px]">{p.name}</span>
              <Badge variant="outline" className="text-[9px]">{p.phase}</Badge>
              <span className="text-red-700 dark:text-red-300 text-[11px]">
                Falta: {p.missing_fichas.map(m => MISSING_LABELS[m] || m).join(", ")}
              </span>
              {p.deviations_count > 0 && (
                <Badge variant="destructive" className="text-[9px]">{p.deviations_count} desviación(es) &gt;10%</Badge>
              )}
            </div>
          ))}
          {data.pending.length > 4 && (
            <div className="text-[11px] text-red-700/70 pt-1">+ {data.pending.length - 4} más…</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
